import React, { useState, useRef, useEffect } from 'react';
import EmailEditor from 'react-email-editor';
import '../styles/footer-designer.css';
import api from '../utils/api';
import { toast } from 'react-toastify';
import EmailPreview from './EmailPreview';
import {
  XMarkIcon,
  EyeIcon,
  CodeBracketIcon,
  CheckCircleIcon,
  AdjustmentsHorizontalIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const TemplateDesigner = ({ initialContent = '', initialDesign = null, onContentChange, onClose, templateData, onTemplateDataChange, onSave }) => {
  const [content, setContent] = useState(initialContent);
  const [viewMode, setViewMode] = useState('split'); // split, editor, preview, code
  const [previewVisible, setPreviewVisible] = useState(false); // Toggle for preview panel - closed by default
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [designSettings, setDesignSettings] = useState({
    backgroundColor: '#f8f9fa',
    containerWidth: '600px',
    fontFamily: 'Arial, sans-serif',
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    textColor: '#666666',
    borderColor: '#dee2e6',
    useGradient: false,
    gradientStartColor: '#667eea',
    gradientEndColor: '#764ba2',
    gradientDirection: 'to right'
  });
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);

  const emailEditorRef = useRef(null);

  // UseEffect to hide Unlayer branding and Dev button
  useEffect(() => {
    const hideUnlayerBranding = () => {
      try {
        // Find the Unlayer iframe
        const iframe = document.querySelector('iframe[id^="editor-"]');
        if (iframe && iframe.contentDocument) {
          const iframeDoc = iframe.contentDocument;

          // Hide branding links - More comprehensive
          const brandingLinks = iframeDoc.querySelectorAll('a[href*="unlayer.com"], a[href*="unlayer"]');
          brandingLinks.forEach(el => {
            if (el.parentElement) {
              el.parentElement.style.display = 'none';
              el.parentElement.style.visibility = 'hidden';
              el.parentElement.style.height = '0';
              el.parentElement.style.overflow = 'hidden';
              el.parentElement.style.opacity = '0';
            }
            el.style.display = 'none';
            el.style.opacity = '0';
          });

          // Hide elements containing "Unlayer" text
          const allElements = iframeDoc.querySelectorAll('*');
          allElements.forEach(el => {
            if (el.textContent && el.textContent.includes('Unlayer')) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.opacity = '0';
            }
          });

          // Hide footer elements  
          const footers = iframeDoc.querySelectorAll('footer, div[class*="footer"], div[class*="Footer"]');
          footers.forEach(el => {
            // Check if it contains Unlayer branding
            if (el.innerHTML && el.innerHTML.includes('Unlayer')) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.height = '0';
              el.style.opacity = '0';
            }
          });

          // Hide Dev button - More comprehensive
          const devButtons = iframeDoc.querySelectorAll(
            'button[title="Dev"], button[title*="Dev"], div[class*="dev"], div[class*="Dev"], button[class*="dev"]'
          );
          devButtons.forEach(el => {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
          });

          // Hide all fixed-position bottom elements (nuclear option)
          const fixedBottomElements = Array.from(iframeDoc.querySelectorAll('*')).filter(el => {
            try {
              const style = iframe.contentWindow.getComputedStyle(el);
              return style.position === 'fixed' && parseInt(style.bottom) <= 20;
            } catch {
              return false;
            }
          });
          fixedBottomElements.forEach(el => {
            // Only hide if it's likely branding (small height, contains text)
            if (el.offsetHeight < 100) {
              el.style.display = 'none';
              el.style.opacity = '0';
            }
          });
        }
      } catch (error) {
        // Ignore cross-origin errors silently
      }
    };

    // Run immediately and then every 500ms to catch dynamically added elements
    hideUnlayerBranding();
    const interval = setInterval(hideUnlayerBranding, 500);

    return () => clearInterval(interval);
  }, []);

  // Initialize email editor with default design
  const onEmailEditorLoad = () => {
    if (emailEditorRef.current) {
      if (initialDesign && Object.keys(initialDesign).length > 0) {
        emailEditorRef.current.editor.loadDesign(initialDesign);
      } else {
        emailEditorRef.current.editor.loadDesign({
          body: {
            rows: [
              {
                cells: [1],
                columns: [
                  {
                    contents: [
                      {
                        type: 'text',
                        values: {
                          containerPadding: '10px',
                          _meta: {
                            htmlID: 'u_content_text_1',
                            htmlClassNames: 'u_content_text'
                          },
                          selectable: true,
                          draggable: true,
                          duplicatable: true,
                          deletable: true,
                          hideable: true,
                          text: initialContent || '<div style="font-size: 14px; line-height: 140%; text-align: center;">\n<p>[[EMAIL_CONTENT]]</p>\n</div>'
                        }
                      }
                    ],
                    values: {
                      backgroundColor: '',
                      padding: '0px',
                      border: {},
                      _meta: { htmlID: 'u_column_1', htmlClassNames: 'u_column' }
                    }
                  }
                ],
                values: {
                  displayCondition: null,
                  columns: false,
                  backgroundColor: '',
                  columnsBackgroundColor: '',
                  backgroundImage: {
                    url: '',
                    fullWidth: true,
                    repeat: false,
                    center: true,
                    cover: false
                  },
                  padding: '0px',
                  hideDesktop: false,
                  _meta: { htmlID: 'u_row_1', htmlClassNames: 'u_row' }
                }
              }
            ],
            values: {
              backgroundColor: designSettings.backgroundColor,
              backgroundImage: {
                url: '',
                fullWidth: true,
                repeat: false,
                center: true,
                cover: false
              },
              contentWidth: designSettings.containerWidth,
              contentAlign: 'center',
              fontFamily: {
                label: 'Arial',
                value: designSettings.fontFamily
              },
              preheaderText: '',
              linkStyle: {
                body: true,
                linkColor: designSettings.primaryColor,
                linkHoverColor: designSettings.secondaryColor,
                linkUnderline: true,
                linkHoverUnderline: true
              },
              _meta: { htmlID: 'u_body', htmlClassNames: 'u_body' }
            }
          }
        });
      }
    }
  };

  // Export HTML from email editor
  const exportHtml = () => {
    if (emailEditorRef.current) {
      setLoading(true);
      emailEditorRef.current.editor.exportHtml(async (data) => {
        const { html } = data;

        // Also save the design JSON
        emailEditorRef.current.editor.saveDesign((design) => {
          try {
            // Unlayer's exported HTML already contains inline styles
            const processedHtml = html;

            setHtmlContent(processedHtml);
            // Pass both HTML and Design JSON to parent
            if (onContentChange) onContentChange(processedHtml, design);
            setContent(processedHtml);
            setHasUnsavedChanges(true);
            setLoading(false);

          } catch (error) {
            console.error('Error processing HTML:', error);
            setHtmlContent(html);
            if (onContentChange) onContentChange(html, design);
            setContent(html);
            setHasUnsavedChanges(true);
            setLoading(false);
          }
        });
      });
    }
  };

  const handleSave = async () => {
    if (!templateData?.name || !templateData?.subject) {
      toast.error('Please provide template name and subject');
      return;
    }

    if (emailEditorRef.current) {
      setLoading(true);
      emailEditorRef.current.editor.exportHtml((data) => {
        const { html } = data;
        emailEditorRef.current.editor.saveDesign(async (design) => {
          try {
            if (onSave) {
              // Update local state for consistency
              if (onContentChange) onContentChange(html, design);
              setHasUnsavedChanges(false);

              // Pass the latest data to the save handler
              await onSave(html, design);
            }
          } catch (error) {
            console.error('Save error:', error);
          } finally {
            setLoading(false);
          }
        });
      });
    }
  };

  // Handle image upload through API
  const handleImageUpload = async (file, done) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `http://localhost:5009${response.data.file.path}`;
      done({ progress: 100, url: imageUrl });

    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      done({ progress: 0 });
    }
  };

  const applyDesignSettings = () => {
    if (emailEditorRef.current) {
      // Determine background color/gradient
      let backgroundValue = designSettings.backgroundColor;

      if (designSettings.useGradient) {
        if (designSettings.gradientDirection === 'radial') {
          backgroundValue = `radial-gradient(circle, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`;
        } else {
          backgroundValue = `linear-gradient(${designSettings.gradientDirection}, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`;
        }
      }

      emailEditorRef.current.editor.setBodyValues({
        contentWidth: designSettings.containerWidth,
        fontFamily: {
          label: designSettings.fontFamily.split(',')[0].trim(),
          value: designSettings.fontFamily
        },
        backgroundColor: backgroundValue
      });

      toast.success('Design settings applied');
      exportHtml();
    }
  };

  const getCodePreview = () => {
    return htmlContent || '<p>No content yet. Start designing your template!</p>';
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-50 flex">
      {/* Global CSS to hide Unlayer branding */}
      <style>{`
        /* Hide "by Unlayer Editor" branding */
        iframe[id*="unlayer"] div[class*="branding"],
        iframe[id*="unlayer"] div[class*="Branding"],
        iframe[id*="unlayer"] a[href*="unlayer.com"],
        iframe[id*="unlayer"] div[class*="footer"] a[href*="unlayer"],
        iframe[id*="unlayer"] div[class*="attribution"],
        iframe div[style*="position: fixed"][style*="bottom"] a {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        /* Hide Dev button */
        iframe[id*="unlayer"] button[title*="Dev"],
        iframe[id*="unlayer"] button[class*="dev"],
        iframe[id*="unlayer"] button[class*="Dev"],
        iframe[id*="unlayer"] div[class*="dev-mode"],
        iframe div[style*="position: fixed"][style*="bottom"] button {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        /* Alternative: Hide all fixed-position bottom elements in iframe */
        iframe div[style*="position:fixed"][style*="bottom:0"],
        iframe div[style*="position: fixed"][style*="bottom: 0"] {
          display: none !important;
        }
      `}</style>

      {/* Sidebar Toggle Button (when collapsed) */}
      {!sidebarVisible && (
        <button
          onClick={() => setSidebarVisible(true)}
          className="absolute left-4 top-4 z-20 p-3 bg-white border border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 transition-all"
          title="Show Sidebar"
        >
          <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Left Sidebar - Settings */}
      {sidebarVisible && (
        <div className="w-80 bg-white shadow-xl overflow-y-auto flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Template Designer</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSidebarVisible(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Hide Sidebar"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Template Settings */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Template Settings
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={templateData?.name || ''}
                  onChange={(e) => onTemplateDataChange({ ...templateData, name: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Welcome Email"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email Subject *</label>
                <input
                  type="text"
                  value={templateData?.subject || ''}
                  onChange={(e) => onTemplateDataChange({ ...templateData, subject: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Welcome to TwilightX Mail!"
                />
              </div>
            </div>
          </div>


          {/* View Mode Selector */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">View Mode</h3>

            <div className="space-y-2">
              <button
                onClick={() => setViewMode('split')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'split' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Side-by-Side
              </button>
              <button
                onClick={() => setViewMode('editor')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'editor' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Editor Only
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'preview' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Preview Only
              </button>
              <button
                onClick={() => setViewMode('code')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'code' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <CodeBracketIcon className="h-4 w-4 mr-2" />
                HTML Code
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b border-gray-200">
            <div className="mb-2 text-xs text-gray-600 font-medium">
              💡 Click to update preview with your latest changes
            </div>
            <button
              onClick={exportHtml}
              disabled={loading}
              className={`w-full px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50 ${hasUnsavedChanges ? 'ring-2 ring-orange-400 ring-offset-2' : ''
                }`}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh Preview'}
              {hasUnsavedChanges && (
                <span className="ml-2 h-2 w-2 bg-orange-500 rounded-full animate-pulse"></span>
              )}
            </button>
          </div>


          {/* Unsaved Changes Warning */}
          {hasUnsavedChanges && (
            <div className="p-4 border-b border-gray-200">
              <div className="px-3 py-2 bg-orange-100 border border-orange-300 rounded-lg flex items-center text-xs text-orange-800">
                <svg className="h-4 w-4 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Unsaved changes! Remember to save.</span>
              </div>
            </div>
          )}

          {/* Save/Cancel Buttons - Integrated into Sidebar */}
          <div className="mt-auto p-4 border-t border-gray-200 bg-gray-50">
            <div className="space-y-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {templateData?.id ? 'Update Template' : 'Save Template'}
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-gray-100">
        {/* Editor & Preview */}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'split' && (
            <div className="h-full flex relative">
              {/* Editor */}
              <div className={`border-r border-gray-300 transition-all duration-300 ${previewVisible ? 'flex-1' : 'w-full'}`}>
                <div className="h-full">
                  <EmailEditor
                    ref={emailEditorRef}
                    onLoad={onEmailEditorLoad}
                    onReady={exportHtml}
                    options={{
                      appearance: {
                        theme: 'light',
                        panels: {
                          tools: {
                            dock: 'right',
                            enabled: false
                          }
                        }
                      },
                      ui: {
                        footer: false // Hide the footer that contains "by Unlayer Editor"
                      },
                      tools: {
                        "custom#devtools_toggle": {
                          enabled: false // Hide the Dev tools button
                        }
                      },
                      features: {
                        imageEditor: true,
                        stockImages: false,
                        preview: false,
                        smartMergeTags: false,
                        colorPicker: {
                          presets: [
                            '#D9E3F0', '#F47373', '#697689', '#37D67A',
                            '#2CCCE4', '#555555', '#DCE775', '#FF6B6B'
                          ],
                          colors: [
                            {
                              id: 'brand_colors',
                              label: 'Brand Colors',
                              colors: [
                                '#007bff', '#6c757d', '#28a745', '#dc3545',
                                '#ffc107', '#17a2b8', '#f8f9fa', '#343a40'
                              ],
                              default: true
                            },
                            {
                              id: 'common_colors',
                              label: 'Common Colors',
                              colors: [
                                'transparent', '#ffffff', '#000000', '#e74c3c', '#3498db',
                                '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c',
                                '#34495e', '#95a5a6', '#d35400', '#c0392b'
                              ]
                            }
                          ]
                        }
                      },
                      tools: {
                        image: {
                          enabled: true,
                          properties: {
                            src: {
                              value: {
                                url: '',
                                width: 200,
                                height: 200,
                                autoWidth: false,
                                maxWidth: '100%'
                              }
                            }
                          }
                        }
                      },
                      customCSS: [
                        'body { font-family: ' + designSettings.fontFamily + '; }',
                        '.blockbuilder-tools-panel { display: none !important; }',
                        '.blockbuilder-panel { display: none !important; }',
                        '.unlayer-branding { display: none !important; visibility: hidden !important; opacity: 0 !important; }',
                        '[class*="branding"] { display: none !important; opacity: 0 !important; }',
                        '[class*="Branding"] { display: none !important; opacity: 0 !important; }',
                        'a[href*="unlayer.com"] { display: none !important; opacity: 0 !important; }',
                        'div[class*="powered"] { display: none !important; opacity: 0 !important; }',
                        'div[class*="Powered"] { display: none !important; opacity: 0 !important; }',
                        'button[title*="Dev"] { display: none !important; opacity: 0 !important; }',
                        'button[class*="dev-mode"] { display: none !important; opacity: 0 !important; }',
                        '[class*="DevMode"] { display: none !important; opacity: 0 !important; }',
                        'div[class*="footer"] a[href*="unlayer"] { display: none !important; opacity: 0 !important; }',
                        'div[class*="Footer"] a[href*="unlayer"] { display: none !important; opacity: 0 !important; }',
                        '[class*="attribution"] { display: none !important; opacity: 0 !important; }',
                        '[class*="Attribution"] { display: none !important; opacity: 0 !important; }',
                        'button[class*="dev"] { display: none !important; opacity: 0 !important; }',
                        'button[class*="Dev"] { display: none !important; opacity: 0 !important; }',
                        'div[style*="position: fixed"][style*="bottom"] a { display: none !important; opacity: 0 !important; }',

                        // Gradient CSS Classes - Available for use in editor
                        // Linear Gradients
                        '.gradient-purple { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-blue { background: linear-gradient(135deg, #6B73FF 0%, #000DFF 100%) !important; }',
                        '.gradient-ocean { background: linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%) !important; }',
                        '.gradient-sunset { background: linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 90%, #2BFF88 100%) !important; }',
                        '.gradient-fire { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important; }',
                        '.gradient-mint { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important; }',
                        '.gradient-peach { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important; }',
                        '.gradient-sky { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%) !important; }',
                        '.gradient-rose { background: linear-gradient(135deg, #f6d365 0%, #fda085 100%) !important; }',
                        '.gradient-emerald { background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%) !important; }',
                        '.gradient-night { background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%) !important; }',
                        '.gradient-warm { background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%) !important; }',
                        '.gradient-cool { background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%) !important; }',
                        '.gradient-cosmic { background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%) !important; }',
                        '.gradient-forest { background: linear-gradient(135deg, #134e5e 0%, #71b280 100%) !important; }',

                        // Radial Gradients
                        '.gradient-radial-purple { background: radial-gradient(circle, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-radial-blue { background: radial-gradient(circle, #6B73FF 0%, #000DFF 100%) !important; }',
                        '.gradient-radial-sunset { background: radial-gradient(circle, #FA8BFF 0%, #2BD2FF 100%) !important; }',

                        // Directional Variations
                        '.gradient-horizontal { background: linear-gradient(to right, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-vertical { background: linear-gradient(to bottom, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-diagonal-up { background: linear-gradient(to top right, #667eea 0%, #764ba2 100%) !important; }',

                        // Custom gradient from design settings
                        '.gradient-custom { background: ' + (designSettings.useGradient
                          ? (designSettings.gradientDirection === 'radial'
                            ? `radial-gradient(circle, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`
                            : `linear-gradient(${designSettings.gradientDirection}, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`)
                          : 'linear-gradient(135deg, #667eea, #764ba2)') + ' !important; }'
                      ],
                      mergeTags: {},
                      displayMode: 'email',
                      locale: 'en-US'
                    }}
                    tools={{
                      image: {
                        enabled: true,
                        properties: {
                          src: {
                            value: {
                              url: '',
                              width: 'auto',
                              height: 'auto',
                              autoWidth: true,
                              maxWidth: '100%'
                            }
                          }
                        }
                      }
                    }}
                    projectId={null}
                    minHeight="100%"
                  />
                </div>
              </div>

              {/* Toggle Preview Button */}
              <button
                onClick={() => setPreviewVisible(!previewVisible)}
                className={`absolute bottom-4 z-10 p-2 bg-white border border-gray-300 rounded-full shadow-lg hover:bg-gray-50 transition-all ${previewVisible ? 'right-4' : 'right-4'
                  }`}
                title={previewVisible ? 'Hide Preview' : 'Show Preview'}
              >
                {previewVisible ? (
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                )}
              </button>

              {/* Preview */}
              {previewVisible && (
                <div className="flex-1 bg-white overflow-auto">
                  <EmailPreview
                    htmlContent={htmlContent}
                    designSettings={designSettings}
                    mode="template"
                  />
                </div>
              )}
            </div>
          )}

          {viewMode === 'editor' && (
            <div className="h-full">
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onEmailEditorLoad}
                onReady={exportHtml}
                options={{
                  appearance: {
                    theme: 'light',
                    panels: {
                      tools: {
                        dock: 'right',
                        enabled: false
                      }
                    }
                  },
                  features: {
                    imageEditor: true,
                    stockImages: false,
                    preview: false
                  },
                  displayMode: 'email',
                  locale: 'en-US'
                }}
                minHeight="100%"
              />
            </div>
          )}

          {viewMode === 'preview' && (
            <div className="h-full bg-white overflow-auto">
              <EmailPreview
                htmlContent={htmlContent}
                designSettings={designSettings}
                mode="template"
              />
            </div>
          )}

          {viewMode === 'code' && (
            <div className="h-full p-6 bg-gray-900 overflow-auto">
              <div className="max-w-6xl mx-auto">
                <div className="bg-gray-800 rounded-lg overflow-hidden shadow-xl">
                  <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gray-900">
                    <h4 className="text-white font-medium">HTML Code</h4>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(getCodePreview());
                        toast.success('Code copied to clipboard');
                      }}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      Copy Code
                    </button>
                  </div>
                  <textarea
                    value={getCodePreview()}
                    readOnly
                    className="w-full p-6 bg-gray-800 text-green-400 font-mono text-sm resize-none border-0 focus:outline-none"
                    style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}
                    spellCheck="false"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateDesigner;